import { Platform } from "react-native";

import { WEB_TITLE } from "@/constants/brand";

/**
 * Web-only in-page fallback for push notifications.
 *
 * Browser/OS delivery of system notifications is unreliable (a push can be
 * accepted by the push service yet never shown). This listens for a message
 * the service worker posts on every push and gives the user an in-app signal
 * regardless of whether the OS notification appeared:
 *
 *   - Visible tab  → nothing audible; the in-app UI already shows the update.
 *   - Hidden tab   → a title/app-icon badge ("(1) Opncue…") always, and a soft
 *                    sound ONLY if the SW could not show a system notification.
 *   - No tab open  → nothing here; only the system push (handled by the SW).
 *
 * That last condition is the whole point and it was missing. The sound used to
 * play on every hidden-tab push, alongside the system notification the SW shows
 * in exactly that case — one message, two alerts (reported on Safari,
 * 2026-09-05). The SW now reports whether the OS notification actually went up,
 * and this chimes only when it did not, which is the case this fallback exists
 * for. The badge is silent, so it is unconditional.
 *
 * A no-op on native and outside a browser.
 */

const SOUND_PREF_KEY = "opncue.notificationSound";

let initialized = false;
let unreadCount = 0;
let audioContext: AudioContext | null = null;

type PushMessage = {
  type: "opncue-push";
  payload: { title?: string; body?: string; data?: Record<string, unknown> };
  /**
   * Whether the service worker put an OS notification on screen for this push.
   * Absent from a worker older than 2026-09-05.1 — a browser can still be
   * running the previous one, since an updated worker only takes over after its
   * activate. Treated as "shown" in that case: the stale worker's behaviour is
   * to show it, so staying quiet matches what the user will actually hear.
   */
  systemNotificationShown?: boolean;
};

function isWeb(): boolean {
  return (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    typeof document !== "undefined"
  );
}

/** The soft in-app sound is on unless the user turned it off. */
export function isNotificationSoundEnabled(): boolean {
  if (!isWeb()) return false;
  try {
    return window.localStorage.getItem(SOUND_PREF_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setNotificationSoundEnabled(enabled: boolean): void {
  if (!isWeb()) return;
  try {
    window.localStorage.setItem(SOUND_PREF_KEY, enabled ? "on" : "off");
  } catch {
    // Storage may be blocked (private mode) — the default (on) still applies.
  }
}

function audioCtor(): typeof AudioContext | undefined {
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  );
}

/** A subtle two-note chime via Web Audio — no asset file needed. */
function playSoftSound(): void {
  if (!isWeb() || !isNotificationSoundEnabled()) return;
  try {
    const Ctor = audioCtor();
    if (!Ctor) return;
    audioContext ??= new Ctor();
    const ctx = audioContext;
    if (ctx.state === "suspended") void ctx.resume();

    const now = ctx.currentTime;
    const notes = [
      { freq: 880, at: 0 },
      { freq: 1174.66, at: 0.12 },
    ];
    for (const { freq, at } of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = now + at;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.08, start + 0.02); // soft peak
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.24);
    }
  } catch {
    // Autoplay / AudioContext restrictions are non-fatal.
  }
}

function stripTitleBadge(title: string): string {
  return title.replace(/^\(\d+\)\s*/, "");
}

function setTitleBadge(count: number): void {
  // The fallback is reached more often than it looks. Under the dev server the
  // document carries TWO title elements — react-helmet emits an empty one
  // before the shell's — and the browser takes the first, so `document.title`
  // is "" and this writes the fallback into the tab. It used to write "opnCue",
  // which is neither the in-app lowercase nor the label capitalisation.
  const base = stripTitleBadge(document.title) || WEB_TITLE;
  document.title = count > 0 ? `(${count}) ${base}` : base;
}

function setAppBadge(count: number): void {
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0) void nav.setAppBadge?.(count);
    else void nav.clearAppBadge?.();
  } catch {
    // Badging API unsupported — ignore.
  }
}

function handlePush(systemNotificationShown: boolean): void {
  // Actively viewing this tab → the in-app UI (inbox, unread badges) already
  // reflects the update, so stay quiet — no sound, no popup.
  if (document.visibilityState === "visible") return;

  // The badge is silent and cumulative, so it is owed either way.
  unreadCount += 1;
  setTitleBadge(unreadCount);
  setAppBadge(unreadCount);

  // The OS already alerted the user. A second sound here is not a fallback.
  if (systemNotificationShown) return;

  playSoftSound();
}

function clearBadges(): void {
  if (unreadCount === 0) return;
  unreadCount = 0;
  setTitleBadge(0);
  setAppBadge(0);
}

/** Registers the SW-message listener and badge-clearing. Safe to call repeatedly. */
export function initWebNotificationFallback(): void {
  if (!isWeb() || initialized) return;
  if (!("serviceWorker" in navigator)) return;
  initialized = true;

  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event.data as PushMessage | undefined;
    if (!data || data.type !== "opncue-push") return;
    handlePush(data.systemNotificationShown ?? true);
  });

  const onFocused = () => {
    if (document.visibilityState === "visible") clearBadges();
  };
  document.addEventListener("visibilitychange", onFocused);
  window.addEventListener("focus", clearBadges);

  // Browsers block audio until the user has interacted with the page. Prime the
  // AudioContext on the first gesture so the backup sound can play even when the
  // tab is later in the background.
  const prime = () => {
    try {
      const Ctor = audioCtor();
      if (Ctor) {
        audioContext ??= new Ctor();
        if (audioContext.state === "suspended") void audioContext.resume();
      }
    } catch {
      // ignore
    }
  };
  window.addEventListener("pointerdown", prime, { once: true });
  window.addEventListener("keydown", prime, { once: true });
}
